import React from 'react';
import { isEmpty } from 'lodash';

import type { DisplaySpec } from '~/components/AiChat/types';
import {
  hasStructuredOutputRenderer,
  StructuredOutputSection,
} from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/StructuredOutputSection';
import { FsIconButton, Text } from '~/components/core';
import { useCollapsible } from '~/components/core/Molecules/Collapsible/useCollapsible';
import Collapse from '~/components/ui/atoms/Collapse';

interface StructuredOutputCardProps {
  structured_output?: Record<string, unknown>;
  display_spec?: DisplaySpec;
}

/**
 * The insight's structured output as its own collapsed section, shaped like References: a
 * titled bar with a chevron, the renderer inside. The analysis text is what a
 * handler reads first; the structured object is the same findings as data, opened on demand.
 * Renders nothing when there is nothing the renderer would show.
 */
export const StructuredOutputCard: React.FC<StructuredOutputCardProps> = ({ structured_output, display_spec }) => {
  const { isOpenCombined, onClick, getChevron } = useCollapsible({ collapsible: true, openByDefault: false });

  if (!structured_output || isEmpty(structured_output) || !display_spec || !hasStructuredOutputRenderer(display_spec)) {
    return null;
  }

  return (
    <section
      aria-label="Structured output"
      className="__STRUCTURED_OUTPUT__ border-4 flex flex-col rounded-lg border-solid border-slate-100 bg-white pl-12"
    >
      <div className="flex items-center justify-between p-4">
        <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD}>
          Structured output
        </Text>
        <FsIconButton
          onClick={onClick}
          className="bg-opacity-75"
          aria-expanded={isOpenCombined}
          aria-label={isOpenCombined ? 'Collapse structured output' : 'Expand structured output'}
        >
          {getChevron({ className: 'w-[14px] h-[14px]' })}
        </FsIconButton>
      </div>
      <Collapse in={isOpenCombined} timeout="auto" unmountOnExit>
        <div className="pb-12 pr-12">
          <StructuredOutputSection structured_output={structured_output} display_spec={display_spec} />
        </div>
      </Collapse>
    </section>
  );
};
